import React, { useState } from 'react';
import SEO from './SEO';
import { useNavigate } from 'react-router-dom';
import { 
  LightBulbIcon,
  ChartBarIcon,
  CogIcon,
  TableCellsIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/20/solid';

const LearnPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (id) => {
    setOpenFaq(openFaq === id ? null : id);
  };

  const faqSections = [
    {
      title: 'Getting Started',
      questions: [
        {
          id: 'gs-1',
          question: 'What are SharpeMetrix Model Portfolios?',
          answer: 'Our Model Portfolios are professionally constructed investment strategies that leverage proprietary alternative data and quantitative signals. They are designed to deliver consistent, risk-adjusted performance and can be easily implemented by advisors or wealth managers.'
        },
        {
          id: 'gs-2',
          question: 'What are PowerLists?',
          answer: 'PowerLists are curated lists of high-conviction stocks for discretionary advisors selected through our data-driven ranking system. They highlight securities with the strongest predictive signals, enabling you to quickly identify potential opportunities.'
        },
        {
          id: 'gs-3',
          question: 'How do I access the platform?',
          answer: 'Once you sign up, you\'ll receive a login to the SharpeMetrix dashboard. From there, you can view portfolios, download reports, and explore PowerLists in real time.'
        },
        {
          id: 'gs-4',
          question: 'Where does your data come from?',
          answer: 'We have partnered with ExtractAlpha (www.extractalpha.com), a leading provider of proprietary alternative data and quantitative signals. This partnership ensures that SharpeMetrix portfolios and PowerLists are powered by institutional-grade insights.'
        }
      ]
    },
    {
      title: 'Features & Use Cases',
      questions: [
        {
          id: 'fuc-1',
          question: 'How can I use Model Portfolios in my practice?',
          answer: 'You can adopt them as turnkey solutions for your clients, integrate them into portfolio sleeves, or use them to build Separately Managed Accounts (SMAs) for clients who want tailored exposure to systematic strategies. They also work as benchmarks or as a foundation for your own custom portfolios.'
        },
        {
          id: 'fuc-2',
          question: 'How do PowerLists benefit me?',
          answer: 'PowerLists give you an actionable shortlist of securities to research further or to validate existing positions, helping you focus on the most promising opportunities rather than sifting through the entire market.'
        },
        {
          id: 'fuc-3',
          question: 'Can I integrate SharpeMetrix with my existing process?',
          answer: 'Yes. Advisors and managers often use our portfolios alongside their current strategies or as a complement to in-house research. Our data-driven approach helps strengthen decision-making without replacing your expertise.'
        },
        {
          id: 'fuc-4',
          question: 'Can I upload my own or client portfolios and watchlists into SharpeMetrix?',
          answer: 'Yes, we have provided advisors the ability to upload their own portfolios and watchlists using the Research Feature. Here, you can create watchlists by either adding individual tickers or uploading CSV files via the upload functionality. This allows advisors to quickly run a health check on their current holdings and validate ideas via the data-driven ranking system.'
        },
        {
          id: 'fuc-5',
          question: 'Which Universes and Benchmarks does SharpeMetrix use?',
          answer: 'We utilize the S&P 500, Russell 1000, Russell 2000, and Russell 3000 as our primary universes and benchmarks. Each model portfolio is constructed within a specific universe and measured against its corresponding benchmark to provide relevant performance comparisons.'
        },
        {
          id: 'fuc-6',
          question: 'Can you explain your Methodology for the Model Portfolios?',
          answer: 'Our methodology combines alternative data with enhanced quantitative factors to build differentiated portfolios. We move beyond traditional financial metrics by incorporating unique signals like analyst estimate quality, earnings call sentiment, and innovation capacity. Our models dynamically adjust for sector and accounting differences, focusing on forward-looking factors such as value, quality, momentum, and volatility. This disciplined approach helps us identify mispriced opportunities and manage risk, providing a strong foundation for long-term outperformance. Our whitepapers are available to clients for further insights.'
        },
        {
          id: 'fuc-7',
          question: 'How do I execute trades at each rebalance?',
          answer: 'At the start of each business day of the month, our models will be rebalanced. We update all signals using the latest available data, and new portfolios are built using updated signals and current market conditions. We publish specific rebalance candidates for each model, which can be exported via CSV for execution by an advisor\'s chosen TAMP, Broker, or Custodian.'
        }
      ]
    },
    {
      title: 'Benefits',
      questions: [
        {
          id: 'ben-1',
          question: 'What makes SharpeMetrix different from other providers?',
          answer: 'We are the only provider offering Model Portfolios built on proprietary alternative data signals traditionally reserved for hedge funds and institutional managers. Traditional quantitative factors like value, quality, and momentum have proven effective over time; but they often suffer from limitations such as using stale data, ignoring sector differences, or capturing unintended market effects. Our enhanced factors address these shortcomings through sophisticated adjustments and forward-looking inputs. This gives you and your clients access to a differentiated edge.'
        },
        {
          id: 'ben-2',
          question: 'How does this help me grow my practice?',
          answer: 'By outsourcing research, portfolio construction, and monitoring to SharpeMetrix, you save valuable time to focus on client relationships and business development. Our differentiated portfolios can also help you stand out in a competitive market.'
        },
        {
          id: 'ben-3',
          question: 'What is the cost of SharpeMetrix? Do you offer different levels of services?',
          answer: 'Pricing is tailored to each advisory firm and is based on factors such as your total assets under management (AUM) and the number of users on your team. Compared to other vendors and model portfolio providers, SharpeMetrix offers an extremely competitive pricing structure, ensuring that you receive institutional-grade portfolios and PowerLists at a fraction of the cost.'
        }
      ]
    },
    {
      title: 'Setup & Support',
      questions: [
        {
          id: 'ss-1',
          question: 'Do I need technical knowledge to get started?',
          answer: 'Not at all. Our portfolios and PowerLists are ready to use out of the box. You\'ll also receive clear reports and guidance on implementation.'
        },
        {
          id: 'ss-2',
          question: 'How often are portfolios and PowerLists updated?',
          answer: 'Portfolios are rebalanced monthly, and PowerLists are refreshed regularly with the latest signals so you always have up-to-date insights.'
        },
        {
          id: 'ss-3',
          question: 'What kind of support can I expect?',
          answer: 'You\'ll have access to email and call support, onboarding assistance, and regular updates on product enhancements. We\'re here to make sure you get the most out of SharpeMetrix.'
        }
      ]
    }
  ];



  const workflowSummary = {
    title: 'Typical Workflow Summary',
    steps: [
      'SharpeMetrix → Publishes model weights & rebalances (via CSV export, with API/platform integration coming soon)',
      'RIA Advisor → Downloads models and pushes to Distribution Platform (Custodian, TAMP, or BD)',
      'Distribution Platform → Receives models and implements account-level trading',
      'RIA Advisor → Selects appropriate model for each client based on suitability',
      'Client Accounts → Trades executed automatically with ongoing drift management and billing'
    ]
  };

  return (
    <div className="min-h-screen bg-bg-100">
      <SEO title="Learn" description="Methodology, quant factors, and platform guides for investment professionals." />
      <div className="px-6 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-100 mb-4">
              SharpeMetrix Documentation
            </h1>
            <p className="text-lg text-text-200">
              Comprehensive guide to our investment methodology, portfolio construction, and platform features.
            </p>
          </div>

          {/* Quick Jump Navigation */}
          <div className="mb-12 bg-bg-200 rounded-lg p-6 border border-bg-300">
            <h2 className="text-lg font-semibold text-text-100 mb-4">Quick Navigation</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => navigate('/learn/methodology')}
                className="text-left p-3 rounded-md bg-bg-100 hover:bg-bg-300 border border-bg-300 hover:border-primary-100 transition-colors group"
              >
                <div className="flex items-center gap-2 mb-1">
                  <DocumentTextIcon className="h-4 w-4 text-primary-100" />
                  <span className="font-medium text-text-100 group-hover:text-primary-100">Methodology</span>
                </div>
                <p className="text-xs text-text-200">Investment approach overview</p>
              </button>
              
              <button
                onClick={() => navigate('/learn/alternative-data')}
                className="text-left p-3 rounded-md bg-bg-100 hover:bg-bg-300 border border-bg-300 hover:border-primary-100 transition-colors group"
              >
                <div className="flex items-center gap-2 mb-1">
                  <LightBulbIcon className="h-4 w-4 text-primary-100" />
                  <span className="font-medium text-text-100 group-hover:text-primary-100">Alternative Data Signals</span>
                </div>
                <p className="text-xs text-text-200">Proprietary signal sources</p>
              </button>
              
              <button
                onClick={() => navigate('/learn/quant-factors')}
                className="text-left p-3 rounded-md bg-bg-100 hover:bg-bg-300 border border-bg-300 hover:border-primary-100 transition-colors group"
              >
                <div className="flex items-center gap-2 mb-1">
                  <ChartBarIcon className="h-4 w-4 text-primary-100" />
                  <span className="font-medium text-text-100 group-hover:text-primary-100">Quant Factors</span>
                </div>
                <p className="text-xs text-text-200">Enhanced factor models</p>
              </button>
              
              <button
                onClick={() => navigate('/learn/portfolio-construction')}
                className="text-left p-3 rounded-md bg-bg-100 hover:bg-bg-300 border border-bg-300 hover:border-primary-100 transition-colors group"
              >
                <div className="flex items-center gap-2 mb-1">
                  <CogIcon className="h-4 w-4 text-primary-100" />
                  <span className="font-medium text-text-100 group-hover:text-primary-100">Portfolio Construction</span>
                </div>
                <p className="text-xs text-text-200">Model building methods</p>
              </button>
              
              <button
                onClick={() => navigate('/learn/platform-guide')}
                className="text-left p-3 rounded-md bg-bg-100 hover:bg-bg-300 border border-bg-300 hover:border-primary-100 transition-colors group"
              >
                <div className="flex items-center gap-2 mb-1">
                  <TableCellsIcon className="h-4 w-4 text-primary-100" />
                  <span className="font-medium text-text-100 group-hover:text-primary-100">Platform Guide</span>
                </div>
                <p className="text-xs text-text-200">Using our tools effectively</p>
              </button>
              
              <button
                onClick={() => navigate('/learn/workflow-integration')}
                className="text-left p-3 rounded-md bg-bg-100 hover:bg-bg-300 border border-bg-300 hover:border-primary-100 transition-colors group"
              >
                <div className="flex items-center gap-2 mb-1">
                  <UserGroupIcon className="h-4 w-4 text-primary-100" />
                  <span className="font-medium text-text-100 group-hover:text-primary-100">Workflow Integration</span>
                </div>
                <p className="text-xs text-text-200">Advisor implementation guide</p>
              </button>
              
              <button
                onClick={() => navigate('/learn/quick-reference')}
                className="text-left p-3 rounded-md bg-bg-100 hover:bg-bg-300 border border-bg-300 hover:border-primary-100 transition-colors group"
              >
                <div className="flex items-center gap-2 mb-1">
                  <DocumentTextIcon className="h-4 w-4 text-primary-100" />
                  <span className="font-medium text-text-100 group-hover:text-primary-100">Quick Reference</span>
                </div>
                <p className="text-xs text-text-200">Key facts and schedules</p>
              </button>
            </div>
          </div>

          {/* Investment Methodology Overview */}
          <div id="methodology-overview" className="mb-12 bg-bg-200 rounded-lg p-6 border border-bg-300">
            <h2 className="text-xl font-semibold text-text-100 mb-4">Investment Methodology</h2>
            <p className="text-text-200 leading-relaxed">
              Our investment methodology combines alternative data and enhanced quantitative factors to deliver differentiated portfolios. 
              We go beyond traditional financial metrics by integrating unique signals such as analyst estimate quality, earnings call sentiment, and innovation capacity.
            </p>
            <p className="text-text-200 leading-relaxed mt-4">
              Our quantitative models are designed to dynamically adjust for sector and accounting differences, focusing on forward-looking value, quality, momentum, and volatility. 
              This disciplined approach aims to identify mispriced opportunities and manage risk effectively, providing a robust foundation for long-term outperformance.
            </p>
          </div>

          {/* Workflow Summary */}
          <div className="mb-12 bg-accent-100/5 rounded-lg p-6 border border-accent-100/20">
            <h3 className="text-lg font-semibold text-text-100 mb-4 flex items-center gap-2">
              <ArrowPathIcon className="h-5 w-5 text-accent-100" />
              {workflowSummary.title}
            </h3>
            <div className="space-y-3">
              {workflowSummary.steps.map((step, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-accent-100/20 rounded-full flex items-center justify-center">
                    <span className="text-xs font-semibold text-accent-100">{index + 1}</span>
                  </div>
                  <p className="text-sm text-text-200 leading-relaxed">{step}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQs Section */}
          <div className="mb-12">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-text-100 mb-2 flex items-center gap-2">
                <QuestionMarkCircleIcon className="h-7 w-7 text-primary-100" />
                Model Portfolios and PowerLists FAQs
              </h2>
              <p className="text-text-300">Common questions about SharpeMetrix products and services</p>
            </div>

            <div className="space-y-6">
              {faqSections.map((section, sectionIdx) => (
                <div key={sectionIdx} className="bg-bg-200 rounded-lg border border-bg-300 overflow-hidden">
                  <div className="px-6 py-4 bg-bg-300/30 border-b border-bg-300">
                    <h3 className="text-lg font-semibold text-text-100">{section.title}</h3>
                  </div>
                  <div className="divide-y divide-bg-300">
                    {section.questions.map((faq) => (
                      <div key={faq.id} className="transition-colors hover:bg-bg-300/20">
                        <button
                          onClick={() => toggleFaq(faq.id)}
                          className="w-full px-6 py-4 flex items-start justify-between text-left focus:outline-none group"
                        >
                          <span className="text-sm font-medium text-text-100 pr-4 group-hover:text-primary-100 transition-colors">
                            {faq.question}
                          </span>
                          <ChevronDownIcon
                            className={`h-5 w-5 text-text-300 flex-shrink-0 transition-transform ${
                              openFaq === faq.id ? 'transform rotate-180' : ''
                            }`}
                          />
                        </button>
                        {openFaq === faq.id && (
                          <div className="px-6 pb-4 pt-0">
                            <p className="text-sm text-text-300 leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Reference */}
          <div id="quick-reference" className="bg-primary-100/5 rounded-lg p-6 border border-primary-100/20">
            <h2 className="text-xl font-semibold text-text-100 mb-4 flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-primary-100" />
              Quick Reference
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-text-100 mb-2">Monthly Schedule</h3>
                <ul className="text-sm text-text-200 space-y-1">
                  <li>• New portfolios released first trading day of each month</li>
                  <li>• Trade lists provided with specific buy/sell instructions</li>
                  <li>• Performance reports available within 2 business days</li>
                  <li>• Signal updates reflect most recent market data</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-text-100 mb-2">Key Metrics</h3>
                <ul className="text-sm text-text-200 space-y-1">
                  <li>• Portfolio sizes: 30-50 stocks typically</li>
                  <li>• Maximum position size: 2-10% depending on strategy</li>
                  <li>• Monthly turnover: 15-25% on average</li>
                  <li>• Universes: S&P 500, Russell 1000, Russell 2000</li>
                </ul>
              </div>
            </div>
          </div>
      </div>
    </div>
  );
};

export default LearnPage; 